import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  fetchCruiseCabinTypes,
  createCruiseCabinType,
  updateCruiseCabinType,
  deleteCruiseCabinType,
  uploadCruiseCabinImage,
  deleteCruiseCabinImage,
} from '../../services/cruiseCabins'
import { toast } from 'react-toastify';

export const getCruiseCabins = createAsyncThunk(
  'cruiseCabins/fetchCruiseCabins',
  async (_, thunkAPI) => {
    try {
      const res = await fetchCruiseCabinTypes()
      if (res.error) throw res.error;
      return res.data
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message })
    }
  },
)

const isFileOrBlob = (obj) => {
  if (!obj) return false;
  return (
    obj instanceof File ||
    obj instanceof Blob ||
    (typeof obj === 'object' && typeof obj.size === 'number')
  );
};

/**
 * Process image uploads for a cruise cabin.
 * - mainImageFile: File | Blob | null → uploads to slot 'main'
 * - galleryFiles: Array<File | Blob | string | null> → index-based slots (up to 5)
 * - previousGalleryUrls: string[] → old URLs to know which slots were removed
 */
async function processCabinImages(cabinId, { mainImageFile, galleryFiles, previousGalleryUrls = [] }) {
  const updates = {}

  // Main image
  if (isFileOrBlob(mainImageFile)) {
    const url = await uploadCruiseCabinImage(cabinId, mainImageFile, 'main')
    if (url) updates.image = url
  }

  // Gallery images (up to 5 additional slots)
  if (Array.isArray(galleryFiles)) {
    const finalUrls = []

    for (let i = 0; i < 5; i++) {
      const item = galleryFiles[i]

      if (isFileOrBlob(item)) {
        const url = await uploadCruiseCabinImage(cabinId, item, `gallery_${i}`)
        if (url) finalUrls.push(url)
      } else if (typeof item === 'string' && item.length > 0 && !item.startsWith('blob:')) {
        finalUrls.push(item)
      } else {
        if (previousGalleryUrls[i]) {
          await deleteCruiseCabinImage(cabinId, `gallery_${i}`)
        }
      }
    }

    updates.gallery_urls = finalUrls
  }

  return updates
}

export const addCruiseCabin = createAsyncThunk(
  'cruiseCabins/addCruiseCabin',
  async (payload, thunkAPI) => {
    try {
      // Support both { cabinData, mainImageFile, galleryFiles } and raw cabinData
      const cabinData = payload.cabinData || payload
      const mainImageFile = payload.mainImageFile || null
      const galleryFiles = payload.galleryFiles || null

      const res = await toast.promise(
        (async () => {
          const insertRes = await createCruiseCabinType(cabinData)
          if (insertRes.error) throw insertRes.error
          const created = insertRes.data[0]

          if (isFileOrBlob(mainImageFile) || (galleryFiles && galleryFiles.some(f => isFileOrBlob(f)))) {
            const imageUpdates = await processCabinImages(created.id, {
              mainImageFile,
              galleryFiles,
              previousGalleryUrls: [],
            })

            if (Object.keys(imageUpdates).length > 0) {
              const updateRes = await updateCruiseCabinType(created.id, imageUpdates)
              if (updateRes.error) throw updateRes.error
              return updateRes
            }
          }

          return insertRes
        })(),
        {
          pending: "Registrando cabina de crucero...",
          success: "Se registró la cabina de crucero 😏",
          error: {
            render({ data: errData }) {
              return errData?.message ? `Error: ${errData.message}` : `Ocurrió un error`
            }
          }
        }
      )
      return res.data[0]
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message })
    }
  }
)

export const editCruiseCabin = createAsyncThunk(
  'cruiseCabins/editCruiseCabin',
  async (payload, thunkAPI) => {
    try {
      // Support both { id, cabinData, mainImageFile, galleryFiles, previousGalleryUrls } and { id, ...cabinData }
      const id = payload.id
      const cabinData = payload.cabinData || (({ id, mainImageFile, galleryFiles, previousGalleryUrls, ...rest }) => rest)(payload)
      const mainImageFile = payload.mainImageFile || null
      const galleryFiles = payload.galleryFiles || null
      const previousGalleryUrls = payload.previousGalleryUrls || []

      const res = await toast.promise(
        (async () => {
          let mergedData = { ...cabinData }

          if (isFileOrBlob(mainImageFile) || galleryFiles) {
            const imageUpdates = await processCabinImages(id, {
              mainImageFile,
              galleryFiles,
              previousGalleryUrls,
            })
            mergedData = { ...mergedData, ...imageUpdates }
          }

          const updateRes = await updateCruiseCabinType(id, mergedData)
          if (updateRes.error) throw updateRes.error
          return updateRes
        })(),
        {
          pending: "Actualizando cabina de crucero...",
          success: "Se actualizó la cabina de crucero 😏",
          error: {
            render({ data: errData }) {
              return errData?.message ? `Error: ${errData.message}` : `Ocurrió un error`
            }
          }
        }
      )
      if (!res.data || res.data.length === 0) {
        return { id, ...cabinData }
      }
      return res.data[0]
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message })
    }
  }
)

export const removeCruiseCabin = createAsyncThunk(
  'cruiseCabins/removeCruiseCabin',
  async (id, thunkAPI) => {
    try {
      await toast.promise(
        deleteCruiseCabinType(id).then((response) => {
          if (response.error) throw response.error;
          return response;
        }),
        {
          pending: "Cargando...",
          success: "Se eliminó la cabina de crucero 😏",
          error: {
            render({ data: errData }) {
              return errData?.message ? `Error: ${errData.message}` : `Ocurrió un error`
            }
          }
        }
      );
      return id
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message })
    }
  }
)
